import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useBrandColour, { useBrandColourForm, useRelations } from '@hooks/useBrandColour'

import ModalSubHeader from '@components/ModalComponents/ModalSubHeader'

const confirmDelete = (deleteBrandColour, deleteFn, closeModal) => {
  const deleteBrandColourPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Brand Colour Deleted',
  }

  swal
    .fire({
      title: 'Delete Brand Colour?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        deleteBrandColour(deleteBrandColourPayload)
      }
    })
}

const createOrUpdateBrandColour = (params) => {
  const {
    entityState, callbacks, createFn, updateFn,
  } = params
  const { closeModal, createBrandColour, updateBrandColour } = callbacks

  const actionType = entityState.id ? updateFn : createFn
  const actionText = entityState.id ? 'Updated' : 'Created'

  const brandColourPayload = {
    callbacks: {
      action: actionType,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Brand Colour ${actionText}`,
  }

  if (entityState.id) return updateBrandColour(brandColourPayload)

  return createBrandColour(brandColourPayload)
}

const CreateOrEditBrandColourModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditBrandColourModal', showModal } = props
  const { closeModal, deleteBrandColour } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { brandColour } = modalPayload

  const {
    callbacks: {
      createBrandColour: createFn,
      updateBrandColour: updateFn,
      deleteBrandColour: deleteFn,
    },
    creating,
    deleting,
    updating,
  } = useBrandColour(brandColour)

  const {
    entityState,
    entityState: {
      cmyk: {
        c, k, m, y,
      },
      id,
      rgb: {
        r, g, b,
      },
      spot_colour_name,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useBrandColourForm(brandColour, { validateOn: 'change' })

  const { brand } = useRelations(brandColour)

  const saveOrCreateButtonText = id ? 'Save Brand Colour' : 'Create Brand Colour'

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit Brand' : 'Add Brand'}
        titleSecondLine="Print Colour"
      />

      <ModalSubHeader title="Managing Brand Print Colour for" ownerTitle={brand.title} />

      <SidebarModal.Content>
        <Form data-validate-field-on="change">
          <Form.Field
            direction="column"
            boxProps={{ marginBottom: 'large' }}
            errorMessage={errors.title}
            label="* Title"
          >
            <input name="title" value={title} type="text" {...handlers} />
          </Form.Field>

          <Form.Field
            direction="column"
            boxProps={{ marginBottom: 'large' }}
            label="Spot Colour Name"
          >
            <input name="spot_colour_name" value={spot_colour_name} type="text" {...handlers} />
          </Form.Field>

          <Box maxWidth="80px" marginBottom="large">
            <Form.Field
              direction="column"
              boxProps={{ marginRight: 'medium' }}
              errorMessage={errors['cmyk.c']}
              hideErrorMessage
              label="* Cyan"
            >
              <input name="cmyk.c" value={c} type="text" {...handlers} />
            </Form.Field>

            <Form.Field
              boxProps={{ marginRight: 'medium' }}
              direction="column"
              errorMessage={errors['cmyk.m']}
              hideErrorMessage
              label="* Magenta"
            >
              <input name="cmyk.m" value={m} type="text" {...handlers} />
            </Form.Field>

            <Form.Field
              direction="column"
              boxProps={{ marginRight: 'medium' }}
              errorMessage={errors['cmyk.y']}
              hideErrorMessage
              label="* Yellow"
            >
              <input name="cmyk.y" value={y} type="text" {...handlers} />
            </Form.Field>

            <Form.Field
              direction="column"
              errorMessage={errors['cmyk.k']}
              hideErrorMessage
              label="* Black"
            >
              <input name="cmyk.k" value={k} type="text" {...handlers} />
            </Form.Field>
          </Box>

          <Box maxWidth="80px" marginBottom="large">
            <Form.Field
              direction="column"
              boxProps={{ marginRight: 'medium' }}
              errorMessage={errors['rgb.r']}
              hideErrorMessage
              label="* Red"
            >
              <input name="rgb.r" value={r} type="text" {...handlers} />
            </Form.Field>

            <Form.Field
              direction="column"
              boxProps={{ marginRight: 'medium' }}
              errorMessage={errors['rgb.g']}
              hideErrorMessage
              label="* Green"
            >
              <input name="rgb.g" value={g} type="text" {...handlers} />
            </Form.Field>

            <Form.Field
              direction="column"
              errorMessage={errors['rgb.b']}
              hideErrorMessage
              label="* Blue"
            >
              <input name="rgb.b" value={b} type="text" {...handlers} />
            </Form.Field>
          </Box>

          {id && (
            <Form.Field direction="column" label="More Options" boxProps={{ marginBottom: 70 }}>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deleteBrandColour, deleteFn, closeModal)}
                size="medium"
              >
                Delete Brand Print Colour
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateBrandColour({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditBrandColourModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditBrandColourModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
